// @flow

import React from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { Section } from '@riseart/layout';
import { ScrollToTopButton } from '@riseart/common';
import { article as ENUM_ARTICLE } from 'Enum';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { BlogArticleQuery } from 'shared_components/article/Query';
import { EmailSubscriptionSection } from 'shared_components/common/subscription/Section';
import { ArticleSection } from 'shared_components/article/Section';
import { RelatedArtworksSection } from 'shared_components/article/RelatedArtworks';
import { FurtherReadingSection } from 'shared_components/article/FurtherReading';
import { MoreCategoriesSection } from 'shared_components/article/MoreCategories';

import {
  blogArticleCls,
  blogArticleScrollWrapperCls,
} from 'shared_components/pages/blog/Article.less';

type Props = {
  staticContext: ?Object,
};

/**
 * BlogArticlePage
 *
 * @param {Props} props
 * @returns {Node}
 */
export default function BlogArticlePage({ staticContext }: Props): Node {
  const { TYPE_POST } = ENUM_ARTICLE.type;
  const { formatMessage } = useIntl();

  return (
    <LayoutDefault scrollTopOnPathChange className={blogArticleCls}>
      <BlogArticleQuery
        staticContext={staticContext}
        articleType={TYPE_POST}
        translationPrefix="blogArticle"
        metaSubscriber={META_SUBSCRIBER_NAME.BLOG_ARTICLE_META}
      >
        {(data) => {
          if (!data.article) {
            return null;
          }

          return (
            <React.Fragment>
              <ArticleSection
                {...data}
                shareContentKey="article"
                listTitle={formatMessage({ id: 'components.breadcrumbs.blog' })}
                listUrl={UrlAssembler.blogList()}
              />
              <EmailSubscriptionSection
                inputId="blog-subscribe-field"
                inputName="blog-subscribe-email"
                title={formatMessage({ id: 'components.blog.subscribe.title' })}
                subTitle={formatMessage({ id: 'components.blog.subscribe.subtitle' })}
                buttonKey="components.blog.subscribe.submitBtn"
                showSuccessDialog
              >
                {({ SubscribeForm }) => (
                  <React.Fragment>
                    <RelatedArtworksSection
                      articleId={data.article.id}
                      {...(!SubscribeForm ? { background: 'gray' } : null)}
                    />
                    {SubscribeForm ? <Section background="gray">{SubscribeForm}</Section> : null}
                  </React.Fragment>
                )}
              </EmailSubscriptionSection>
              <FurtherReadingSection
                queryVars={{
                  ...(data.articleCategory && data.articleCategory.slug
                    ? { category: data.articleCategory.slug }
                    : null),
                  type: TYPE_POST,
                  ignoreArticleIds: data.article.id,
                }}
                title={formatMessage({ id: 'components.blog.furtherReading' })}
              />
              <MoreCategoriesSection />
              <div className={blogArticleScrollWrapperCls}>
                <ScrollToTopButton />
              </div>
            </React.Fragment>
          );
        }}
      </BlogArticleQuery>
    </LayoutDefault>
  );
}
